import React from 'react';
import { cn } from '@/shared/utils/cn';
import type { Meridiem } from '../utils/datetimeFormat';

export interface SegmentFieldProps {
  value: string;
  placeholder: string;
  maxLength: number;
  /** Ancho del segmento en modo inline; omitir en modo `boxed`. */
  widthClass?: string;
  /** Segmento dentro de {@link TimeSegmentBox} (popup). */
  boxed?: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (value: string) => void;
  onCommit: () => void;
  onFocus?: () => void;
  inputRef?: React.Ref<HTMLInputElement>;
}

/** Campo numérico de un segmento (fecha/hora); compartido entre input y popup. */
export const SegmentField = ({
  value,
  placeholder,
  maxLength,
  widthClass,
  boxed = false,
  disabled,
  ariaLabel,
  onChange,
  onCommit,
  onFocus,
  inputRef,
}: SegmentFieldProps) => (
  <input
    ref={inputRef}
    type="text"
    inputMode="numeric"
    aria-label={ariaLabel}
    disabled={disabled}
    value={value}
    placeholder={placeholder}
    maxLength={maxLength}
    onFocus={onFocus}
    onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, maxLength))}
    onBlur={onCommit}
    onKeyDown={(event) => {
      if (event.key === 'Enter') {
        event.currentTarget.blur();
      }
    }}
    className={cn(
      'ui-border-0 ui-bg-transparent ui-p-0 ui-text-center ui-text-inherit ui-outline-none',
      'ui-tabular-nums',
      boxed ? 'ui-w-full' : 'focus:ui-bg-surface-brand-default focus:ui-rounded-control-xs',
      'disabled:ui-text-disabled',
      !boxed && widthClass
    )}
  />
);

export const DateTimeSeparator = ({ children }: { children: React.ReactNode }) => (
  <span className="ui-select-none ui-text-tertiary" aria-hidden>
    {children}
  </span>
);

/** Separador `:` del panel de hora (tipografía lg según Figma). */
export const TimePickerColon = () => (
  <span
    className={cn(
      'ui-flex ui-shrink-0 ui-self-stretch ui-items-center ui-justify-center',
      'ui-select-none ui-text-body-lg-regular ui-font-body-lg-regular ui-leading-body-lg-regular ui-tracking-body-lg',
      'ui-text-tertiary'
    )}
    aria-hidden
  >
    :
  </span>
);

/** Caja 48×48 que envuelve un segmento en el popup de hora (Figma). */
export const TimeSegmentBox = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      'ui-flex ui-h-control-lg ui-w-control-lg ui-shrink-0 ui-items-center ui-justify-center',
      'ui-rounded-control-sm ui-border ui-border-default ui-bg-fill ui-p-8',
      'ui-text-body-sm-regular ui-font-body-sm-regular ui-leading-body-sm-regular ui-tracking-body-sm ui-text-primary',
      'focus-within:ui-border-brand focus-within:ui-bg-surface-brand-default',
      className
    )}
  >
    {children}
  </div>
);

export interface MeridiemToggleStackProps {
  value: Meridiem;
  disabled?: boolean;
  onSelect: (meridiem: Meridiem) => void;
}

/** Toggle AM/PM vertical (popup); orden PM arriba, AM abajo (Figma). */
export const MeridiemToggleStack = ({ value, disabled, onSelect }: MeridiemToggleStackProps) => {
  const options: Meridiem[] = ['PM', 'AM'];

  return (
    <div className="ui-flex ui-flex-col ui-gap-8 ui-justify-center" role="group" aria-label="AM o PM">
      {options.map((option) => {
        const isSelected = value === option;
        return (
          <button
            key={option}
            type="button"
            disabled={disabled}
            aria-pressed={isSelected}
            onClick={() => onSelect(option)}
            className={cn(
              'ui-flex ui-h-control-xxs ui-items-center ui-justify-center',
              'ui-rounded-control-sm ui-px-12 ui-py-2',
              'ui-text-body-xs-bold ui-font-body-xs-bold ui-leading-body-xs-bold ui-tracking-body-xs',
              'ui-transition-colors',
              disabled && 'ui-cursor-not-allowed ui-opacity-50',
              isSelected
                ? 'ui-bg-surface-brand-default ui-text-brand'
                : 'ui-bg-fill-tertiary ui-text-primary hover:ui-bg-fill-tertiary-hover active:ui-bg-fill-tertiary-pressed'
            )}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
};
